import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";
import { useGetFinixJobHealthQuery } from "store/queries/finixOps";
import { IFinixCostCompleteness, IFinixJobHealthItem } from "types/FinixOps";

import { formatDateTime } from "../../utils/hstImports";

// Below this share of settled card sales with complete costs, the /costs endpoint has probably stopped working.
export const COST_COMPLETENESS_WARNING_SHARE = 0.9;

/**
 * A run summary as short text: scalar values as `key: value`, lists by their length. Summaries hold counts and
 * ids only (the API's contract), so nothing here can be a name.
 */
export const summaryText = (summary: Record<string, unknown> | null) => {
  if (!summary) return "";
  return Object.entries(summary)
    .map(([key, value]) => {
      if (Array.isArray(value)) return `${key}: ${value.length}`;
      if (value === null || value === undefined) return `${key}: —`;
      if (typeof value === "object") return null;
      return `${key}: ${String(value)}`;
    })
    .filter(Boolean)
    .join(" · ");
};

const outcomeBody = (job: IFinixJobHealthItem) => {
  if (!job.lastRun) return <span className="text-600">No run recorded</span>;
  return (
    <div className="flex align-items-center gap-2">
      <Tag
        value={job.lastRun.outcome === "SUCCEEDED" ? "Succeeded" : "Failed"}
        severity={job.lastRun.outcome === "SUCCEEDED" ? "success" : "danger"}
      />
      {job.overdue ? (
        <Tag
          value="Overdue"
          severity="warning"
          data-testid={`finix-job-overdue-${job.key}`}
        />
      ) : null}
    </div>
  );
};

const summaryBody = (job: IFinixJobHealthItem) => {
  if (!job.lastRun) return null;
  return (
    <div className="flex flex-column gap-1">
      <small style={{ wordBreak: "break-word" }}>
        {summaryText(job.lastRun.summary)}
      </small>
      {job.lastRun.error ? (
        <small className="text-red-600" style={{ wordBreak: "break-word" }}>
          {job.lastRun.error}
        </small>
      ) : null}
    </div>
  );
};

function CostCompleteness({ value }: { value: IFinixCostCompleteness }) {
  const percent =
    value.share === null ? null : Math.round(value.share * 1000) / 10;
  const low =
    value.share !== null && value.share < COST_COMPLETENESS_WARNING_SHARE;
  return (
    <div
      className="flex flex-column gap-2"
      data-testid="finix-cost-completeness"
    >
      <div>
        <span className="font-semibold">Cost completeness: </span>
        {percent === null ? "no card sales in the window" : `${percent}%`}
        <span className="text-600">
          {" "}
          ({value.complete} of {value.sales} card sales {value.minAgeDays}–
          {value.windowDays} days old have all three cost categories)
        </span>
      </div>
      {low ? (
        <Message
          severity="warn"
          text="Fewer sales than usual have complete costs: the /costs endpoint or its credential may have stopped working, and the weekly economics report is understated."
        />
      ) : null}
    </div>
  );
}

export function FinixJobHealthSection() {
  const { data, isFetching } = useGetFinixJobHealthQuery();
  const health = data?.data;

  return (
    <div className="flex flex-column gap-3">
      <DataTable
        value={health?.jobs || []}
        dataKey="key"
        loading={isFetching}
        emptyMessage="No job runs recorded yet."
        responsiveLayout="scroll"
      >
        <Column header="Job" field="label" />
        <Column header="Schedule" field="cadence" />
        <Column
          header="Last run"
          body={(job: IFinixJobHealthItem) =>
            job.lastRun ? formatDateTime(job.lastRun.startedAt) : "—"
          }
        />
        <Column header="Outcome" body={outcomeBody} />
        <Column header="Summary" body={summaryBody} />
        <Column
          header="Last failure"
          body={(job: IFinixJobHealthItem) => formatDateTime(job.lastFailedAt)}
        />
      </DataTable>
      {health?.costCompleteness ? (
        <CostCompleteness value={health.costCompleteness} />
      ) : null}
      <small className="text-600">
        Runs are recorded from this release on; a job with no run recorded has
        not run since.
      </small>
    </div>
  );
}
